
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import Rocket from 'plaid-threads/Icons/Rocket';
import Libraries from 'plaid-threads/Icons/Libraries';
import Overview from '../../../components/docs/Overview';
import Actions from '../../../components/docs/Actions';
export const metadata = {
  toc: true,
  maxTocLevel: 4,
  layout: 'guide',
  description: 'Sanction, PEP, and watchlist screening for anti-money laundering compliance'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const YouTubeEmbed = makeShortcode("YouTubeEmbed");
const Callout = makeShortcode("Callout");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <Overview.Header product={'Monitor'}>{metadata.description}</Overview.Header>
    <Actions.SmallWrapper miniTitle='Get started with Monitor'>
  <Actions.SmallCard icon={<Libraries mdxType="Libraries" />} title='API Reference' link='/docs/api/products/monitor/' />
  <Actions.SmallCard icon={<Rocket mdxType="Rocket" />} title='Get started' link='https://dashboard.plaid.com/support/new/admin/account-administration/request-product-access' />
    </Actions.SmallWrapper>
    <h4 {...{
      "id": "overview"
    }}>{`Overview`}</h4>
    <p>{`Monitor detects if your customers are on government watchlists, using modern APIs that reduce false positives and increase efficiency. Monitor screens users against a number of sanction and PEP lists. Sanction lists are updated daily and can be used for daily automated re-scans. Any hits will be exposed via both a user-friendly dashboard UI and via API, for use with either manual or automated review workflows. Monitor also integrates directly with `}<a parentName="p" {...{
        "href": "/docs/identity-verification"
      }}>{`Identity Verification`}</a>{` for an end-to-end verification and KYC solution.`}</p>
    <p>{`Monitor can be used to screen end users in any country. To integrate with Monitor, your company must be based in the US, Canada, or UK.`}</p>
    <YouTubeEmbed videoId='uzpg75DZ28Q' alignment='full' title='Plaid Monitor in 3 minutes' mdxType="YouTubeEmbed" />
    <h4 {...{
      "id": "creating-a-program"
    }}>{`Creating a program`}</h4>
    <Callout mdxType="Callout">
      <p>{`Monitor is not available by default in the Sandbox environment. To obtain Sandbox access, `}<a parentName="p" {...{
          "href": "https://dashboard.plaid.com/settings/team/products"
        }}>{`request full Production access for Monitor`}</a>{`, which will automatically grant Sandbox access, or `}<a parentName="p" {...{
          "href": "https://plaid.com/products/monitor/#contactForm"
        }}>{`contact sales`}</a>{`. To obtain Sandbox access for Monitor if you are already using another Plaid product, you can also contact your account manager or submit an `}<a parentName="p" {...{
          "href": "https://dashboard.plaid.com/support/new/admin/account-administration/request-product-access"
        }}>{`access request ticket`}</a>{`.`}</p>
    </Callout>
    <p>{`The first step to integrating is logging into the dashboard and accessing the `}<strong parentName="p">{`New Program`}</strong>{` page. Programs help you to define the settings and workflows for conducting watchlist screenings in your organization. For example, you can define match sensitivities, which lists are being screened, and the behavior of ongoing screening. For more details on the configuration of screenings and how matches are determined, see `}<a parentName="p" {...{
        "href": "/docs/monitor/algorithms-and-lists/"
      }}>{`Watchlists and Matching Algorithms`}</a>{`.`}</p>
    <p>{`Monitor allows you to start a customer in one program and then move them to a different one. To move users between programs, call `}<inlineCode parentName="p">{`/watchlist_screening/individual/update`}</inlineCode>{` with the `}<inlineCode parentName="p">{`watchlist_screening_id`}</inlineCode>{` you want to move and the ID of the program you want to move it to in `}<inlineCode parentName="p">{`search_terms.watchlist_program_id`}</inlineCode>{`, or call `}<inlineCode parentName="p">{`/watchlist_screening/entity/update`}</inlineCode>{` with the `}<inlineCode parentName="p">{`entity_watchlist_screening_id`}</inlineCode>{` you want to move and the ID of the program you want to move it to in `}<inlineCode parentName="p">{`search_terms.entity_watchlist_program_id`}</inlineCode>{`. Customers cannot be in multiple programs at once. `}</p>
    <p>{`The number of programs you wish to maintain will vary based on the complexity of your organization. Many companies choose to split up programs such that each program aligns to the compliance ownership within their organization and each reviewer will only see programs they are responsible for. Some common ways of splitting up programs are by product vertical ("Cardholders", "Personal Loans", "FDIC Accounts", etc.), geography ("US Cardholders", "European Cardholders", etc.), risk level ("High Risk Individuals", "Medium Risk Individuals"), or a combination of the above. For ideas about how to split customers up by risk, `}<a parentName="p" {...{
        "href": "https://cognitohq.com/blog/ofac-risk-matrix"
      }}>{`OFAC has prepared a helpful risk matrix`}</a>{` that can provide a baseline for setting initial risk level and then modifying the risk level based on ongoing user behavior. You are allowed to change a program after it has been created, although note that increasing the sensitivity of a program after creation may result in a large influx of new hits.`}</p>
    <p>{`If your use case involves an ongoing relationship with your customers, Plaid recommends creating at least two programs -- one with rescan enabled for active customers, and one with rescan disabled, for customers who have not yet established an ongoing relationship (for example, customers who have not completed the onboarding experience) or who have closed their accounts. For more details, see `}<a parentName="p" {...{
        "href": "/docs/account/billing/#monitor-fee-model"
      }}>{`Monitor fee model`}</a>{`.`}</p>
    <p>{`Once your first program is created, you can start your integration.`}</p>
    <h4 {...{
      "id": "basic-integration-overview"
    }}>{`Basic integration overview`}</h4>
    <p>{`Before integrating Monitor, make sure you have `}<a parentName="p" {...{
        "href": "#creating-a-program"
      }}>{`Created a program`}</a>{`.`}</p>
    <p>{`This integration overview describes the process for using a Dashboard-based review workflow, which is the most common way to use Monitor. The review process can also be performed using the API; see the `}<a parentName="p" {...{
        "href": "/docs/api/products/monitor"
      }}>{`API reference`}</a>{` for details.`}</p>
    <p>{`If you are already using Plaid's `}<a parentName="p" {...{
        "href": "/docs/identity-verification"
      }}>{`Identity Verification`}</a>{` product:`}</p>
    <ol>
      <li parentName="ol">{`In the Identity Verification template editor, on the Setup tab, enable the `}<strong parentName="li">{`Screen customers`}</strong>{` setting to automatically assign users to a Monitor program after they have completed verification.`}</li>
      <li parentName="ol">{`When your user has successfully completed the Identity Verification process, the response you receive from calling `}<inlineCode parentName="li">{`/identity_verification/get`}</inlineCode>{` will include a `}<inlineCode parentName="li">{`watchlist_screening_id`}</inlineCode>{` value.`}</li>
      <li parentName="ol">{`Call `}<inlineCode parentName="li">{`/watchlist_screening/individual/get`}</inlineCode>{` with this value, and check the `}<inlineCode parentName="li">{`status`}</inlineCode>{` flag on the response.`}
        <ul parentName="li">
          <li parentName="ul">{`If it is `}<inlineCode parentName="li">{`cleared`}</inlineCode>{`, your customer did not appear on any of your target screening lists.`}</li>
          <li parentName="ul">{`If it is `}<inlineCode parentName="li">{`pending_review`}</inlineCode>{`, your customer received one or more hits and is pending review.`}</li>
        </ul>
      </li>
      <li parentName="ol">{`For customers who are pending review, a member of your team will need to review and update the user's status in the Dashboard. When they do (and you have configured your webhooks as `}<a parentName="li" {...{
          "href": "#configuring-webhooks"
        }}>{`described below`}</a>{`) you will receive a webhook, `}<inlineCode parentName="li">{`SCREENING: STATUS_UPDATED`}</inlineCode>{` (or `}<inlineCode parentName="li">{`ENTITY_SCREENING: STATUS_UPDATED`}</inlineCode>{` for entities). This webhook will include the `}<inlineCode parentName="li">{`screening_id`}</inlineCode>{` of the individual (or `}<inlineCode parentName="li">{`entity_screening_id`}</inlineCode>{` for entities).`}</li>
      <li parentName="ol">{`You can then make an additional call to `}<inlineCode parentName="li">{`/watchlist_screening/individual/get`}</inlineCode>{` (or `}<inlineCode parentName="li">{`/watchlist_screening/entity/get`}</inlineCode>{`, if you created an entity screening) and check the `}<inlineCode parentName="li">{`status`}</inlineCode>{` flag on the response.`}
        <ul parentName="li">
          <li parentName="ul">{`If it is `}<inlineCode parentName="li">{`cleared`}</inlineCode>{`, the screening hit has been dismissed by a reviewer as invalid.`}</li>
          <li parentName="ul">{`If it is `}<inlineCode parentName="li">{`pending_review`}</inlineCode>{`, a customer that was previously marked as `}<inlineCode parentName="li">{`cleared`}</inlineCode>{` or `}<inlineCode parentName="li">{`rejected`}</inlineCode>{` was marked for review.`}</li>
          <li parentName="ul">{`If it is `}<inlineCode parentName="li">{`rejected`}</inlineCode>{`, your customer received a hit that has been confirmed by a reviewer as a valid match.`}</li>
        </ul>
      </li>
    </ol>
    <p>{`If you are not using Identity Verification, the process is slightly different:`}</p>
    <ol>
      <li parentName="ol">{`Create a customer object using `}<inlineCode parentName="li">{`/watchlist_screening/individual/create`}</inlineCode>{` or `}<inlineCode parentName="li">{`/watchlist_screening/entity/create`}</inlineCode>{`, passing along any verified information you have about your user. While not required, it is recommended to specify a `}<inlineCode parentName="li">{`client_user_id`}</inlineCode>{`, which should refer to an internal ID in your database, to help associate this screening with your customer in the future.`}</li>
      <li parentName="ol">{`The results of this `}<inlineCode parentName="li">{`create`}</inlineCode>{` call will include a `}<inlineCode parentName="li">{`status`}</inlineCode>{` flag -- either `}<inlineCode parentName="li">{`cleared`}</inlineCode>{`, or `}<inlineCode parentName="li">{`pending_review`}</inlineCode>{` as specified above.`}</li>
      <li parentName="ol">{`At this point, you can proceed from step 4 in the above workflow.`}</li>
    </ol>
    <h4 {...{
      "id": "configuring-webhooks"
    }}>{`Configuring webhooks`}</h4>
    <p>{`To complete your integration, you should add a webhook receiver endpoint to your application.`}</p>
    <p>{`Like Identity Verification, you can configure the webhooks sent by Monitor by visiting the dashboard `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/developers/webhooks"
      }}>{`webhook configuration`}</a>{` page. Click `}<strong parentName="p">{`New webhook`}</strong>{`, and then select which events you want to subscribe to. For Monitor, there are two:`}</p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/docs/api/products/monitor/#screening-status_updated"
        }}><inlineCode parentName="a">{`SCREENING: STATUS_UPDATED`}</inlineCode></a>{` when an individual's screening status has been updated`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/docs/api/products/monitor/#entity_screening-status_updated"
        }}><inlineCode parentName="a">{`ENTITY_SCREENING: STATUS_UPDATED`}</inlineCode></a>{` when an entity's screening status has been updated`}</li>
    </ul>
    <p>{`Enter the URL of your webhook receiver for the webhook you wish to subscribe to and click `}<strong parentName="p">{`Configure`}</strong>{`. Plaid will now send an HTTP POST request to the webhook receiver endpoint every time the event occurs.`}</p>
    <p>{`For more information on webhooks, see the `}<a parentName="p" {...{
        "href": "/docs/api/webhooks"
      }}>{`webhook documentation`}</a>{`.`}</p>
    <h4 {...{
      "id": "building-a-reviewer-workflow"
    }}>{`Building a reviewer workflow`}</h4>
    <p>{`This guide assumes that you will be using the dashboard for reviewing hits, but you can also re-create any aspect of the dashboard in your internal applications via the `}<a parentName="p" {...{
        "href": "/docs/api/products/monitor"
      }}>{`API`}</a>{`.`}</p>
    <h5 {...{
      "id": "assigning-hits-to-reviewers"
    }}>{`Assigning hits to reviewers`}</h5>
    <p>{`Once your basic integration is complete, any customers who have potential hits will start showing up on your dashboard. You can assign any screenings that are pending review to different reviewers in your organization. If using an API-based review workflow, you can get a full list of users and their associated user IDs by calling `}<inlineCode parentName="p">{`/dashboard_user/list`}</inlineCode>{` and setting your screening `}<inlineCode parentName="p">{`assignee`}</inlineCode>{` to the desired reviewer.`}</p>
    <h4 {...{
      "id": "preparing-for-ongoing-screening"
    }}>{`Preparing for ongoing screening`}</h4>
    <p>{`Monitor updates screening lists daily and supports ongoing daily rescans of your entire customer base to alert you when new hits are discovered. This system is designed around the concept of a living `}<inlineCode parentName="p">{`pending_review`}</inlineCode>{` queue that is updated whenever new hits are found. We recommend that a reviewer log in daily to check the review status on the dashboard. If you prefer to be alerted when a hit is found or to handle hits via automation, you can set up a daily automated job to check `}<inlineCode parentName="p">{`/watchlist_screening/individual/hit/list`}</inlineCode>{` or `}<inlineCode parentName="p">{`/watchlist_screening/entity/hit/list`}</inlineCode>{` once a day to poll any of the screenings that end up in this queue. Then use the associated `}<inlineCode parentName="p">{`client_user_id`}</inlineCode>{` or `}<inlineCode parentName="p">{`id`}</inlineCode>{` to tie the hit back to your internal database and determine if action is required.`}</p>
    <p>{`Plaid will also fire `}<a parentName="p" {...{
        "href": "/docs/api/products/monitor/"
      }}>{`webhooks`}</a>{` to alert you when any new hits are found. Webhooks will contain the `}<inlineCode parentName="p">{`screening_id`}</inlineCode>{` (or `}<inlineCode parentName="p">{`entity_screening_id`}</inlineCode>{` for entity screenings), which you can use to go retrieve any watchlist screening hits that are pending review.`}</p>
    <h4 {...{
      "id": "generating-reports"
    }}>{`Generating reports`}</h4>
    <p>{`You can generate and export reports on Monitor activity via the Dashboard, under `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/monitor/reports"
      }}>{`Monitor->Reports`}</a>{`. Reports are provided in .csv format.`}</p>
    <p>{`You can also generate reports using the Monitor API. For example, you can use `}<inlineCode parentName="p">{`/watchlist_screening/individual/list`}</inlineCode>{` or `}<inlineCode parentName="p">{`/watchlist_screening/entity/list`}</inlineCode>{` to generate the list of individuals or entities you'd like to report on, then call `}<inlineCode parentName="p">{`/watchlist_screening/individual/hit/list`}</inlineCode>{` or `}<inlineCode parentName="p">{`/watchlist_screening/entity/hit/list`}</inlineCode>{` for each target individual or entity to generate a list of their hits.`}</p>
    <h4 {...{
      "id": "additional-considerations"
    }}>{`Additional considerations`}</h4>
    <ul>
      <li parentName="ul">{`Adding Monitor is not the only part of a successful Anti-Money Laundering program. We highly recommend that you consult with an AML professional to help you build all of the policies and procedures required. AML regulations vary widely for different jurisdictions and industries.`}</li>
      <li parentName="ul">{`Monitor `}<strong parentName="li">{`does not blanket ban any countries on your behalf`}</strong>{`. Some sanction programs effectively prohibit working with certain jurisdictions and it is up to you to ensure that you are only working with individuals from countries that are appropriate for your desired risk levels. For instance, here is an `}<a parentName="li" {...{
          "href": "https://ofac.treasury.gov/sanctions-programs-and-country-information"
        }}>{`article from OFAC about a "Country List"`}</a></li>
      <li parentName="ul">{`Monitor is built to make it difficult to overconstrain a search and therefore have false negatives. That being said, if your data inputs are not quality controlled to at least a basic extent, this can result in false negatives or false positives (for example, broad searches based on incomplete names).`}</li>
    </ul>
    <h4 {...{
      "id": "testing-monitor"
    }}>{`Testing Monitor`}</h4>
    <Callout mdxType="Callout">
      <p>{`Monitor is not available by default in the Sandbox environment. To obtain Sandbox access, `}<a parentName="p" {...{
          "href": "https://dashboard.plaid.com/settings/team/products"
        }}>{`request full Production access for Monitor`}</a>{`, which will automatically grant Sandbox access, or `}<a parentName="p" {...{
          "href": "https://plaid.com/products/monitor/#contactForm"
        }}>{`contact sales`}</a>{`. To obtain Sandbox access for Monitor if you are already using another Plaid product, you can also contact your account manager or submit an `}<a parentName="p" {...{
          "href": "https://dashboard.plaid.com/support/new/admin/account-administration/request-product-access"
        }}>{`access request ticket`}</a>{`.`}</p>
    </Callout>
    <p>{`Sandbox supports a real-world dataset, meaning that sanctioned individuals should show up just as they would in Production. Some example sanctioned individuals and entities you can use for testing purposes include:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Individual Name`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Date of Birth`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Location`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Document`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Joseph Kony`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`1964-09-18`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`CF`}</td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Jianshu Zhou`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`1971-07-15`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`CN`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`E09598913`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Ermias Ghermay`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Any day in 1980`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`LY`}</td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
      </tbody>
    </table>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Entity Name`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Location`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Document`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Iran Mobin Electronic Development Company`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`IR`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`10103492170`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`МИНСКИЙ ОТРЯД МИЛИЦИИ ОСОБОГО НАЗНАЧЕНИЯ`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`BY`}</td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Islamic Radio And Television Union`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`AF`}</td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
      </tbody>
    </table>
    <p>{`Testing Monitor alongside Identity Verification can be a little tricky, as Monitor screens against a real-world dataset, while Identity Verification screens against Sandbox test identities such as Leslie Knope or any `}<a parentName="p" {...{
        "href": "/docs/identity-verification/testing/#custom-test-users"
      }}>{`custom test users`}</a>{` you have configured. If you wish to test the case of Identity Verification succeeding, but a Monitor screening encountering a hit, you can try one of these options:`}</p>
    <ul>
      <li parentName="ul">{`Create a `}<a parentName="li" {...{
          "href": "/docs/identity-verification/testing/#custom-test-users"
        }}>{`custom test user`}</a>{` in the Identity Verification Dashboard using the name and date of birth of a real sanctioned individual from the tables above. The session will pass the Data Source check and return a Monitor hit. Note that the first and last name must match the watchlist entry exactly, and that Documentary Verification will fail for this user, since Sandbox documents always reflect the Leslie Knope test identity -- use a template that does not require a document name or date of birth match.`}</li>
      <li parentName="ul">{`From the Monitor section of the Dashboard, select the user from the `}<strong parentName="li">{`Cleared`}</strong>{` tab and change their status from "Cleared" to "Pending Review" or "Rejected". This will fire a `}<inlineCode parentName="li">{`STATUS_UPDATED`}</inlineCode>{` webhook, and the user's new screening status will be reflected in the response the next time you call `}<inlineCode parentName="li">{`/watchlist_screening/individual/get`}</inlineCode>{`.`}</li>
      <li parentName="ul">{`You can also create additional watchlist screenings against the same client user ID by calling `}<inlineCode parentName="li">{`/watchlist_screening/individual/create`}</inlineCode>{` with the name and birthday of a real sanctioned individual. This screening will appear in the Monitor dashboard in the "Pending review" section and will be included in the array of screening results that get returned if you call `}<inlineCode parentName="li">{`/watchlist_screening/individual/list`}</inlineCode>{` with this client user ID.`}</li>
    </ul>
    <p>{`Note that the Sandbox environment sanction and watchlist dataset may be out of date. Make sure all real checks are carried out on the live Production environment.`}</p>
    <h4 {...{
      "id": "monitor-pricing"
    }}>{`Monitor pricing`}</h4>
    <p>{`Monitor has a base fee for each new user scanned, as well as a separate rescanning fee, determined by the number of users rescanned each month. For more details, see the `}<a parentName="p" {...{
        "href": "/docs/account/billing/#monitor-fee-model"
      }}>{`Monitor fee model`}</a>{`. To view the exact pricing you may be eligible for, `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/overview/production"
      }}>{`apply for Production access`}</a>{` or `}<a parentName="p" {...{
        "href": "https://plaid.com/contact/"
      }}>{`contact sales`}</a>{`. `}</p>
    <h4 {...{
      "id": "next-steps"
    }}>{`Next steps`}</h4>
    <p>{`To learn more about building with Monitor, see the `}<a parentName="p" {...{
        "href": "/docs/api/products/monitor"
      }}>{`Monitor API Reference`}</a>{`.`}</p>
    <p>{`If you're ready to launch to Production, see the `}<a parentName="p" {...{
        "href": "/docs/launch-checklist/"
      }}>{`Launch checklist`}</a>{`.`}</p>
    <Actions.Wrapper>
  <Actions.Card icon={<Rocket mdxType="Rocket" />} title='Launch Center' subtitle="See next steps to launch in Production" link='https://dashboard.plaid.com/developers/launch-center' linkText='Launch' />
    </Actions.Wrapper>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "overview",
        level: 4,
        title: "Overview",
      },
{
        id: "creating-a-program",
        level: 4,
        title: "Creating a program",
      },
{
        id: "basic-integration-overview",
        level: 4,
        title: "Basic integration overview",
      },
{
        id: "configuring-webhooks",
        level: 4,
        title: "Configuring webhooks",
      },
{
        id: "building-a-reviewer-workflow",
        level: 4,
        title: "Building a reviewer workflow",
      },
{
        id: "preparing-for-ongoing-screening",
        level: 4,
        title: "Preparing for ongoing screening",
      },
{
        id: "generating-reports",
        level: 4,
        title: "Generating reports",
      },
{
        id: "additional-considerations",
        level: 4,
        title: "Additional considerations",
      },
{
        id: "testing-monitor",
        level: 4,
        title: "Testing Monitor",
      },
{
        id: "monitor-pricing",
        level: 4,
        title: "Monitor pricing",
      },
{
        id: "next-steps",
        level: 4,
        title: "Next steps",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
